import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { deleteMethod, getMethod } from "../../services/ApiCallMethods";

// Thunk to get investors associated with an advisor
export const getInvestorForAdvisorByDocuments = createAsyncThunk(
  "advisorDocuments/getInvestorByDocuments",
  async ({ advisor_id, search, page = 1, limit = 10, investment, investment_amount }, { rejectWithValue }) => {
    try {
      // Build query parameters dynamically
      let queryParams = `advisor_id=${advisor_id}&search=${search}&page=${page}&limit=${limit}`;

      // Only add investment parameters if they have values
      if (investment) {
        queryParams += `&investments=${investment}`;
      }

      if (investment_amount) {
        queryParams += `&investment_amount=${investment_amount}`;
      }

      const response = await getMethod(
        `/advisor/getClientsAssociatedToAdvisorForWebAdmin?${queryParams}`
      );
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch investors");
    }
  }
);

// Thunk to get documents associated with an advisor
export const getDocumentsAssociatedToAdvisor = createAsyncThunk(
  "advisorDocuments/getDocumentsAssociatedToAdvisor",
  async ({ advisor_id, accountType, account,ownerEntity, search }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();

      if (advisor_id) params.append("advisor_id", advisor_id);
      if (accountType) params.append("accountType", accountType);
      if (account) params.append("account", account);
       if (ownerEntity) params.append("ownerEntity", ownerEntity);
      if (search) params.append("search", search);

      const url = `/advisor/getDocumentsAssociatedToAdvisorForWebAdmin?${params.toString()
        }`;

      const response = await getMethod(url);
      const data = await response.json();

      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch documents");
    }
  }
);

export const getPropertiesAssociatedToAdvisorForClient = createAsyncThunk(
  "advisorDocuments/getPropertiesAssociatedToAdvisorForClient",
  async ({ advisor_id, search, page = 1, itemsPerPage = 10 }, { rejectWithValue }) => {
    try {
      const response = await getMethod(
        `/advisor/getPropertiesAssociatedToAdvisorForClient/${advisor_id}?search=${search}&page=${page}&limit=${itemsPerPage}`
      );
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch properties");
    }
  }
);
export const deleteDocumentsofClientsAdvisor = createAsyncThunk(
  "advisorDocuments/deleteDocumentsofClientsAdvisor",
  async ({ advisor_id, document_id }, { rejectWithValue }) => {
    try {
      const response = await deleteMethod(
        `/advisor/${advisor_id}/client/document/${document_id}`
      );
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch documents");
    }
  }
);
export const getDocumentsofClientsAdvisor = createAsyncThunk(
  "advisorDocuments/getDocumentsofClientsAdvisor",
  async ({ advisor_id,clientId, search = "", account_type = "", account = "", timeframe = "" }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams({
        search,
        account_type,
        account,
        timeframe,
        advisorId:clientId
      });

      const response = await getMethod(
        `/advisor/getAdvisorRelatedDocumentsToClient/${advisor_id}?${queryParams.toString()}`
      );

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch documents");
    }
  }
);

export const deleteDocumentsofAdminAdvisors = createAsyncThunk(
  "advisorDocuments/deleteDocumentsofAdminAdvisors",
  async ({ document_id }, { rejectWithValue }) => {
    try {
      const response = await deleteMethod(
        `/advisor/deleteDocumentAndMappings/${document_id}`
      );
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch documents");
    }
  }
);

// Initial state
const initialState = {
  investors: [],
  advisorDocuments: [],
  property: [],
  clientDocuments: [],
  loading: false,
  error: null,
};

// Slice
const advisorDocumentSlice = createSlice({
  name: "advisorDocuments",
  initialState,
  reducers: {
    resetInvestorDocuments: (state) => {
      state.investors = [];
      state.property = [];
      state.advisorDocuments = [];
      state.clientDocuments = [];
      state.loading = false;
      state.message = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Investors Thunk
      .addCase(getInvestorForAdvisorByDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInvestorForAdvisorByDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.investors = action.payload?.clients || [];
        state.investorsPagination = action.payload?.pagination || []
      })
      .addCase(getInvestorForAdvisorByDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })
      // Documents Thunk
      .addCase(getDocumentsAssociatedToAdvisor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDocumentsAssociatedToAdvisor.fulfilled, (state, action) => {
        state.loading = false;
        state.advisorDocuments = action.payload?.documents || [];
      })
      .addCase(getDocumentsAssociatedToAdvisor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })
      // Properties thunk
      .addCase(getPropertiesAssociatedToAdvisorForClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getPropertiesAssociatedToAdvisorForClient.fulfilled,
        (state, action) => {
          state.loading = false;
          state.property = action.payload?.data || [];
          state.propertyPagination = action.payload?.pagination || {
            currentPage: 1,
            itemsPerPage: 10,
            totalPages: 1,
            totalItems: 0,
          };
        }
      )
      .addCase(
        getPropertiesAssociatedToAdvisorForClient.rejected,
        (state, action) => {
          state.loading = false;
          state.error = action.payload || "Something went wrong";
        }
      )
      .addCase(deleteDocumentsofClientsAdvisor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDocumentsofClientsAdvisor.fulfilled, (state, action) => {
        state.loading = false;
        // state.clientDocuments = state.clientDocuments.filter(
        //   (doc) => doc.id !== action.payload.document_id
        // );
        state.message =
          action.payload?.message || "Document deleted successfully";
      })
      .addCase(deleteDocumentsofClientsAdvisor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete document";
      })
      .addCase(deleteDocumentsofAdminAdvisors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDocumentsofAdminAdvisors.fulfilled, (state, action) => {
        state.loading = false;
        state.message =
          action.payload?.message || "Document deleted successfully";
      })
      .addCase(deleteDocumentsofAdminAdvisors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete document";
      })
      .addCase(getDocumentsofClientsAdvisor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDocumentsofClientsAdvisor.fulfilled, (state, action) => {
        state.loading = false;
        state.clientDocuments = action.payload?.data || [];
      })
      .addCase(getDocumentsofClientsAdvisor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      });
  },
});

export const { resetInvestorDocuments } = advisorDocumentSlice.actions;
export default advisorDocumentSlice.reducer;
